import { useEffect } from "react";

interface SEOProps {
  title: string;
  description?: string;
  keywords?: string;
  siteName?: string;
}

const SITE_NAME = "Surprize la Domiciliu";
const DEFAULT_TITLE = "Surprize la Domiciliu în România | Livrare în Toată România";
const PRIMARY_DOMAIN = "surprizeladomiciliu.ro";

function isPrimaryDomain() {
  return window.location.hostname === PRIMARY_DOMAIN;
}

function setMeta(selector: string, attr: string, value: string) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [attrName, attrVal] = selector.replace("meta[", "").replace("]", "").split('="');
    el.setAttribute(attrName, attrVal.replace('"', ""));
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

function applyMeta(title: string, description: string | undefined, keywords: string | undefined, siteName: string) {
  document.title = title;

  // Always point canonical to primary domain
  const pathname = window.location.pathname === "/" ? "/" : window.location.pathname.replace(/\/+$/, "");
  const canonicalUrl = `https://${PRIMARY_DOMAIN}${pathname}`;
  let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", canonicalUrl);

  // Add noindex on non-primary domains
  if (!isPrimaryDomain()) {
    setMeta('meta[name="robots"]', "content", "noindex, nofollow");
  } else {
    const robotsMeta = document.querySelector('meta[name="robots"]');
    if (robotsMeta) robotsMeta.remove();
  }

  setMeta('meta[property="og:url"]', "content", canonicalUrl);
  setMeta('meta[property="og:title"]', "content", title);
  setMeta('meta[name="twitter:title"]', "content", title);
  setMeta('meta[property="og:site_name"]', "content", siteName);

  if (description) {
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[name="twitter:description"]', "content", description);
  }

  if (keywords) {
    setMeta('meta[name="keywords"]', "content", keywords);
  }
}

export const useSEO = ({ title, description, keywords, siteName = SITE_NAME }: SEOProps) => {
  // Set synchronously on first render for crawlers
  applyMeta(title, description, keywords, siteName);

  useEffect(() => {
    applyMeta(title, description, keywords, siteName);
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [title, description, keywords, siteName]);
};

